import { useCallback, useEffect, useState } from 'react';
import type { EmblaCarouselType } from 'embla-carousel';

import { cn } from '@utils/cn';

import { CarouselNextButton, CarouselPrevButton, usePrevNextButtons } from './CarouselButtons';
import type { CarouselNavigationType } from './index';

export function useCarouselPagination(emblaApi: EmblaCarouselType | undefined) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onPaginationClick = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const onInit = useCallback((api: EmblaCarouselType) => {
    setScrollSnaps(api.scrollSnapList());
  }, []);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onPaginationClick,
  };
}

type CarouselDotNavigationProps = {
  emblaApi?: EmblaCarouselType;
  iconOnly?: boolean;
  type?: CarouselNavigationType;
};

export function CarouselDotNavigation({
  emblaApi,
  iconOnly = false,
  type,
}: CarouselDotNavigationProps) {
  const { selectedIndex, scrollSnaps, onPaginationClick } = useCarouselPagination(emblaApi);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi);

  if (scrollSnaps.length === 1) return null;

  return (
    <div className={cn('w-full flex flex-row justify-between gap-2 mt-4')}>
      {type === 'arrows' && (
        <CarouselPrevButton
          disabled={prevBtnDisabled}
          iconOnly={iconOnly}
          isAbsolute={false}
          isMobileVisible
          onClick={onPrevButtonClick}
        />
      )}

      <div className={cn('flex flex-row flex-wrap gap-1 justify-center items-center flex-1')}>
        {scrollSnaps.map((_, index) => {
          const isSelected = selectedIndex === index || (!selectedIndex && index === 0);

          return (
            <button
              aria-current={isSelected ? 'true' : undefined}
              aria-label={`Ga naar slide ${index + 1}`}
              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-0 p-0"
              key={index}
              onClick={() => onPaginationClick(index)}
              type="button"
            >
              <span
                className={cn(
                  'h-[10px] w-[10px] rounded-full transition-colors',
                  isSelected ? 'bg-primary' : 'bg-secondary/55',
                  'hover:bg-primary',
                )}
              />
            </button>
          );
        })}
      </div>

      {type === 'arrows' && (
        <CarouselNextButton
          disabled={nextBtnDisabled}
          iconOnly={iconOnly}
          isAbsolute={false}
          isMobileVisible
          onClick={onNextButtonClick}
        />
      )}
    </div>
  );
}
